import {
  Stack,
  Typography,
  Switch,
  Input,
  SizedBox,
  Tooltip,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { ReorderIcon } from '@fortress-technology-solutions/fortress-component-library/Icons';
import { FormattedMessage } from 'react-intl';
import type { ScreeningProviderControlsProps } from './types';
import messages from './messages';
import {
  validateDefaultProfile,
  hasOverlappingScreeningMethods,
} from '../../utils';

function ScreeningProviderControls({
  editMode,
  data,
  onChange,
  activeProviderCount,
  activeProviders = [],
  dragHandleProps,
}: ScreeningProviderControlsProps) {
  if (!data) {
    return null;
  }

  const resolvedActiveProviderCount =
    activeProviderCount ?? activeProviders.length;

  const codeError = validateDefaultProfile(data);
  const isNotConfigured = !data.isEnabled;
  const hasConflict =
    data.isEnabled &&
    !data.isActive &&
    hasOverlappingScreeningMethods(data, activeProviders);
  const isSwitchDisabled = isNotConfigured || hasConflict;

  const switchElement = (
    <Switch
      checked={data.isActive}
      disabled={isSwitchDisabled}
      onChange={() => {
        const newIsActiveValue = !data.isActive;
        const orderValue = newIsActiveValue
          ? resolvedActiveProviderCount + 1
          : 0;

        onChange({
          target: {
            name: `screeningSetupData.${data.providerName}.isActive`,
            value: newIsActiveValue,
          },
        });
        onChange({
          target: {
            name: `screeningSetupData.${data.providerName}.order`,
            value: orderValue,
          },
        });
      }}
    />
  );

  return (
    <Stack direction="row" spacing={20}>
      <Stack direction="row" spacing={1}>
        {editMode && dragHandleProps && (
          <div
            role="button"
            style={{ cursor: 'grab' }}
            aria-label={`Drag ${data.providerName} to reorder`}
            {...dragHandleProps.attributes}
            {...dragHandleProps.listeners}
          >
            <ReorderIcon
              color="action"
              sx={{
                '&:focus': {
                  outline: 'none',
                },
              }}
            />
          </div>
        )}
        <Stack spacing={1}>
          <Typography fontWeight="bold">{data.providerName}</Typography>
          {editMode ? (
            isNotConfigured ? (
              <Tooltip
                title={<FormattedMessage {...messages.notConfiguredTooltip} />}
                placement="right"
              >
                <span>{switchElement}</span>
              </Tooltip>
            ) : hasConflict ? (
              <Tooltip
                title={
                  <FormattedMessage {...messages.overlappingMethodsTooltip} />
                }
                placement="right"
              >
                <span>{switchElement}</span>
              </Tooltip>
            ) : (
              switchElement
            )
          ) : data.isActive ? (
            <FormattedMessage {...messages.on} />
          ) : (
            <FormattedMessage {...messages.off} />
          )}
        </Stack>
      </Stack>
      {data.hasConfig && (
        <Stack spacing={1}>
          <Typography
            fontWeight="bold"
            sx={editMode ? { marginBottom: 2 } : {}}
          >
            {data.providerName} <FormattedMessage {...messages.code} />*
          </Typography>
          {editMode ? (
            <>
              <SizedBox h={0.1} />
              <Input
                sx={{
                  height: 8,
                }}
                inputProps={{
                  sx: { color: 'black', width: 200, height: 8 },
                }}
                value={data.defaultProfile}
                onChange={(e) => {
                  onChange({
                    target: {
                      name: `screeningSetupData.${data.providerName}.defaultProfile`,
                      value: e.target.value,
                    },
                  });
                }}
                error={codeError}
              />
            </>
          ) : (
            <Typography>{data.defaultProfile}</Typography>
          )}
        </Stack>
      )}
    </Stack>
  );
}

export default ScreeningProviderControls;
